import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MStarValidators } from '@morningstar/shared/validators';
import { Store } from '@ngrx/store';
import { merge, of, Subject } from 'rxjs';
import { catchError, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import * as MeetingAttendeesActions from '../+state/meeting-attendees/meeting-attendees.actions';
import { MeetingAttendeesPartialState } from '../+state/meeting-attendees/meeting-attendees.reducer';
import { getEmail, getMeeting, getMeetingId } from '../+state/meeting-attendees/meeting-attendees.selectors';
import { MeetingAttendeesService } from '../meeting-attendee.service';
import { MeetingCheckInSteps } from '../meeting-check-in-steps';

@Component({
  selector: 'mstar-meeting-attendee-register',
  templateUrl: './meeting-attendee-register.component.html',
  styleUrls: ['./meeting-attendee-register.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MeetingAttendeeRegisterComponent implements OnDestroy {

  // == STATE =====================================================

  processingRegistration = new Subject<boolean>();
  processingRegistration$ = this.processingRegistration.asObservable();

  form = this.formBuilder.group({
    firstName: [null, [Validators.required, Validators.maxLength(50)]],
    lastName: [null, [Validators.required, Validators.maxLength(50)]],
    email: [null, [Validators.required, Validators.maxLength(100)]],
    phoneNumber: [null, [MStarValidators.phone, Validators.maxLength(20)]]
  });

  meetingId$ = this.store.select(getMeetingId);
  meeting$ = this.store.select(getMeeting);
  email$ = this.store.select(getEmail);

  // == ACTIONS ===================================================

  registerAttendee = new Subject();
  registerAttendee$ = this.registerAttendee.asObservable();

  // == SIDE EFFECTS ==============================================  

  onEmail$ = this.email$.pipe(
    tap(email => this.form.patchValue({ email }))
  );

  onRegisterAttendee$ = this.registerAttendee$.pipe(
    withLatestFrom(this.meetingId$),
    tap(() => this.processingRegistration.next(true)),
    mergeMap(([_, meetingId]) => this.meetingAttendeeService.registerAttendee({
      ...this.form.value,
      arrived: true,
      meetingId
    }).pipe(
      catchError((error: HttpErrorResponse) => of(error))
    )),
    tap(error => {      
      this.processingRegistration.next(false);
      
      if (error instanceof HttpErrorResponse) {
        this.snackbar.open('Error checking in', 'DISMISS', { duration: 5000 });
        return;
      }

      this.store.dispatch(MeetingAttendeesActions.checkedIn());
    })
  );

  // == SUBSCRIPTION ==============================================

  subscription = merge(
    this.onRegisterAttendee$,
    this.onEmail$
  ).subscribe();

  // == LIFECYCLE =================================================
    
  constructor(private formBuilder: FormBuilder,
              private store: Store<MeetingAttendeesPartialState>,
              private snackbar: MatSnackBar,
              private meetingAttendeeService: MeetingAttendeesService) { }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  // == HELPERS ===================================================

  register() {
    this.registerAttendee.next();
  } 

  back() {
    this.store.dispatch(MeetingAttendeesActions.goToStep({ 
      step: MeetingCheckInSteps.attendeeCheckIn 
    }))
  }
}
